// ChatGPT-style conversation management for the Ask Lilia panel.
//
// The server (ai_conversations/ai_messages) is the source of truth; this
// localStorage layer is a single, USER-scoped cache for instant paint. It is
// deliberately NOT keyed by document any more — conversations follow the user
// across the library and every document (each conversation carries its own
// `documentId` scope tag), so history never "vanishes" when you open a doc.
// The `documentId` params below are kept for call-site compatibility but ignored.

export interface StoredConversation<T = unknown> {
  id: string;
  title: string;
  turns: T[];
  updatedAt: number;
  /** Scope this conversation belongs to: a document id, or null = library/general.
   * Reassignable ("promote"/continue-here) via the server `move` endpoint. */
  documentId?: string | null;
  /** Server conversation id once persisted. Absent = local draft (no messages
   * yet). The server is the source of truth; this cache enables instant paint. */
  serverId?: string;
  /** True once the full thread has been hydrated from the server. */
  hydrated?: boolean;
  /** The user named this thread by hand (design handoff §6). Two consequences:
   * the list sync must not overwrite the title with the server's auto-name, and
   * a thread with no messages yet is still worth keeping as a draft — naming a
   * thread before typing in it is a normal thing to do now that the title is a
   * one-click control. */
  titleLocked?: boolean;
}

// The LIST is one global (user-scoped) cache — no per-document silo, so history
// follows you across the library and every document. That is deliberate.
//
// Which conversation is ACTIVE is per document, which it was not until
// 2026-09-06: both keys ignored their documentId argument, so there was a
// single global "last active" and opening any document dropped you into
// whatever chat you were last in — frequently another document's. The list
// being shared is a feature; the selection being shared was a bug.
const listKey = (_documentId?: string) => `lilia.ask.conversations.all`;
const activeKey = (documentId?: string) => `lilia.ask.active.${documentId ?? "library"}`;
const MAX_CONVERSATIONS = 60;

export function newConversationId(): string {
  return `c_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`;
}

/** Derive a short title from the first user message in the thread. */
export function titleFromTurns(turns: Array<{ role: string; text?: string }>): string {
  const firstUser = turns.find((t) => t.role === "user" && t.text);
  const raw = (firstUser?.text ?? "").trim().replace(/\s+/g, " ");
  if (!raw) return "New chat";
  return raw.length > 42 ? `${raw.slice(0, 42)}…` : raw;
}

export function loadConversations<T>(documentId?: string): StoredConversation<T>[] {
  try {
    const raw = window.localStorage.getItem(listKey(documentId));
    const parsed = raw ? (JSON.parse(raw) as StoredConversation<T>[]) : [];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

export function saveConversations<T>(documentId: string | undefined, list: StoredConversation<T>[]): void {
  try {
    // Newest first, capped — drop the oldest beyond the cap.
    const trimmed = [...list].sort((a, b) => b.updatedAt - a.updatedAt).slice(0, MAX_CONVERSATIONS);
    window.localStorage.setItem(listKey(documentId), JSON.stringify(trimmed));
  } catch {
    /* quota / private mode — non-fatal */
  }
}

export function loadActiveId(documentId?: string): string | null {
  try {
    return window.localStorage.getItem(activeKey(documentId));
  } catch {
    return null;
  }
}

export function saveActiveId(documentId: string | undefined, id: string | null): void {
  try {
    if (id) window.localStorage.setItem(activeKey(documentId), id);
    else window.localStorage.removeItem(activeKey(documentId));
  } catch {
    /* non-fatal */
  }
}
